// Copyright ©️ 2026 eVolve MEP, LLC

import type { BendTypeName } from '@evolvemep/api-ts';

/**
 * A map of bend types to an SVG path representing them.
 * Read here for a quick reference on how to build an SVG path
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorials/SVG_from_scratch/Paths
 *
 * Each bend is a quadratic curve. Most shapes are drawn from the front. The kick 90s bend in two planes, which a front
 * view can't show, so they're drawn from the preview's iso camera: the tail runs up-right along the floor, kicks up,
 * then turns its stub level to the left or right.
 */
const BendTypeIconMap = {
  'Stub 90': 'M2 30H40Q52 30 52 18V2',
  'Offset': 'M2 28L19 28Q25 28 28.6 23.2L36.4 12.8Q40 8 46 8L62 8',
  'Kick 90 Left': 'M15.2 29.5L38.6 20.1Q43.2 18.2 45 13.5L47 8.5Q48.8 3.9 43.8 3.5L31.8 2.5',
  'Kick 90 Right': 'M5.4 29.5L30.2 19.5Q34.8 17.6 36.7 12.9L38.9 7.2Q40.7 2.5 45.7 2.9L58.6 3.9',
  'Angled': 'M2 28L27 28Q34 28 39.7 23.9L62 8',
  '3-Point Saddle': 'M2 28L13 28Q18 28 20.9 23.9L29.7 11.3Q32 8 34.3 11.3L43.1 23.9Q46 28 51 28L62 28',
  '4-Point Saddle': 'M2 28L10 28Q15 28 17.1 23.5L22.9 11.5Q25 7 30 7L34 7Q39 7 41.1 11.5L46.9 23.5Q49 28 54 28L62 28',
  'Offset Back to Back': 'M2 28L25 28Q32 28 32 21L32 11Q32 4 39 4L62 4',
  'Kick 90 Back to Back Right': 'M6 29.5L33.7 18.3Q38.4 16.4 38.4 11.4L38.4 7.5Q38.4 2.5 43.3 2.9L58 4.1',
  'Kick 90 Back to Back Left': 'M16.7 29.5L42.6 19Q47.3 17.2 47.3 12.2L47.3 9Q47.3 4 42.3 3.6L28.7 2.5',
  // An offset up over an obstruction, then a 90 up into a box.
  'Custom': 'M2 29L10 29Q14 29 16.8 26.2L21.2 21.8Q24 19 28 19L46.3 19Q54 19 54 11.3L54 3',
} as const satisfies Record<BendTypeName, string>;

export const BendTypeIcon = ({ bendType }: { bendType: BendTypeName }) => (
  <svg viewBox="0 0 64 32" width="64" height="32">
    <path d={BendTypeIconMap[bendType]} fill="none" stroke="black" strokeWidth="2" />
  </svg>
);
